import { createRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Route as AdminLayoutRoute } from "./_layout";
import { Badge, BadgeWithDot } from "@/components/base/badges/badges";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Card, CardBody, CardHeader } from "@/components/application/card/card";
import { DataTable, type Column } from "@/components/application/data-table/data-table";
import { apiCall } from "../../lib/apiClient";

interface BillingGroupRow {
  id: string;
  code: string;
  display_name: string;
  tenant_id: string;
  deactivated_at: string | null;
  zone_id: string | null;
  zone_code: string | null;
  client_zone_url: string | null;
  zone_has_credentials: boolean;
  payment_account_id: string | null;
  payment_account_label: string | null;
}

interface BillingGroupListResponse {
  rows: BillingGroupRow[];
}

interface PaymentAccountOption {
  account_id: string;
  label: string;
}

export function BillingGroupsPage() {
  const [rows, setRows] = useState<BillingGroupRow[]>([]);
  const [accounts, setAccounts] = useState<PaymentAccountOption[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // id skupiny, u které právě probíhá uložení přiřazení účtu.
  const [assigningId, setAssigningId] = useState<string | null>(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [bg, pa] = await Promise.all([
        apiCall<BillingGroupListResponse>("/api/admin/billing-groups"),
        apiCall<{ accounts: PaymentAccountOption[] }>("/api/admin/payment-accounts"),
      ]);
      setRows(bg.rows);
      setAccounts(pa.accounts);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : "Načtení seznamu Středisek selhalo.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  const assignAccount = useCallback(
    async (billingGroupId: string, paymentAccountId: string | null) => {
      setAssigningId(billingGroupId);
      setLoadError(null);
      try {
        await apiCall(`/api/admin/billing-groups/${billingGroupId}/payment-account`, {
          method: "PUT",
          body: JSON.stringify({ payment_account_id: paymentAccountId }),
        });
        await fetchRows();
      } catch (e) {
        setLoadError(e instanceof Error ? e.message : "Přiřazení platebního účtu selhalo.");
      } finally {
        setAssigningId(null);
      }
    },
    [fetchRows],
  );

  const columns = useMemo<Column<BillingGroupRow>[]>(
    () => [
      {
        key: "code",
        header: "Kód",
        cell: (r) => <strong className="text-primary">{r.code}</strong>,
      },
      {
        key: "name",
        header: "Název",
        cell: (r) => r.display_name,
      },
      {
        key: "zone",
        header: "Klientská zóna",
        cell: (r) => {
          if (!r.zone_id) {
            return (
              <BadgeWithDot color="warning">
                Nepřiřazena
              </BadgeWithDot>
            );
          }
          return (
            <div className="flex flex-col gap-1">
              <BadgeWithDot color={r.zone_has_credentials ? "success" : "warning"}>
                {r.zone_has_credentials ? "Nastavena" : "Chybí přihlašovací údaje"}
              </BadgeWithDot>
              {r.client_zone_url && (
                <div className="text-xs text-tertiary truncate" title={r.client_zone_url}>
                  {shortenUrl(r.client_zone_url)}
                </div>
              )}
            </div>
          );
        },
      },
      {
        key: "account",
        header: "Platební účet",
        cell: (r) => (
          <select
            className="block w-full min-h-touch rounded-lg bg-primary px-3 py-2 text-md text-primary ring-1 ring-inset ring-primary outline-hidden focus:ring-2 focus:ring-brand disabled:opacity-50"
            value={r.payment_account_id ?? ""}
            disabled={assigningId === r.id}
            onChange={(e) =>
              void assignAccount(r.id, e.target.value === "" ? null : e.target.value)
            }
            aria-label={`Platební účet pro středisko ${r.code}`}
            data-testid={`billing-group-account-${r.id}`}
          >
            <option value="">— nepřiřazen —</option>
            {accounts.map((a) => (
              <option key={a.account_id} value={a.account_id}>
                {a.label}
              </option>
            ))}
          </select>
        ),
      },
      {
        key: "status",
        header: "Stav",
        cell: (r) =>
          r.deactivated_at ? (
            <Badge color="gray">Deaktivováno</Badge>
          ) : (
            <BadgeWithDot color="success">
              Aktivní
            </BadgeWithDot>
          ),
      },
      {
        key: "detail",
        header: "Detail",
        cell: (r) => (
          <Link
            to="/admin/billing-groups/$id"
            params={{ id: r.id }}
            className="text-brand-secondary font-medium hover:underline"
            data-testid={`billing-group-detail-${r.id}`}
          >
            Lidé a karty
          </Link>
        ),
      },
    ],
    [accounts, assignAccount, assigningId],
  );

  return (
    <section aria-labelledby="billing-groups-title" className="max-w-7xl mx-auto space-y-6">
      <header className="flex flex-col gap-1">
        <h2 id="billing-groups-title" className="text-2xl font-bold text-primary m-0">
          Střediska
        </h2>
        <p className="text-base text-tertiary m-0">
          Klientská zóna Axigonu per Středisko a napojení na tenanty + platební účty.
        </p>
      </header>

      {loadError && <InlineAlert variant="error">{loadError}</InlineAlert>}

      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold text-primary m-0">
            Seznam Středisek
            {!loading && <span className="ml-2 text-sm font-normal text-tertiary">({rows.length})</span>}
          </h3>
        </CardHeader>
        <CardBody>
          <DataTable
            caption="Seznam Středisek se stavem klientské zóny a platebního účtu"
            columns={columns}
            rows={rows}
            rowKey={(r) => r.id}
            loading={loading}
            emptyMessage="Žádná Střediska zatím nejsou zaregistrována."
          />
        </CardBody>
      </Card>
    </section>
  );
}

function shortenUrl(url: string): string {
  try {
    const u = new URL(url);
    return u.host + u.pathname.replace(/\/$/, "");
  } catch {
    return url;
  }
}

export const Route = createRoute({
  getParentRoute: () => AdminLayoutRoute,
  path: "/admin/billing-groups",
  component: BillingGroupsPage,
});
